import React, { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import {
  accordionSummaryStyles, ButtonWithIcon,
  cardStyles,
  CommitteeAddressBlock,
  useGlobalMessageHandler
} from 'components'
import { type CommitteeAddress, type CommitteeBasicInformation } from 'services'
import { getEmptyCommitteeAddress } from 'helpers'

interface CommitteeEditProps {
  originalAddress: CommitteeAddress []
  loading: boolean
  updateBasicInfo: (newData: CommitteeBasicInformation) => void
  originalBasicInfo: CommitteeBasicInformation
}

export const CommitteeAddressesCard: React.FC<CommitteeEditProps> = ({ originalAddress, loading, updateBasicInfo, originalBasicInfo }) => {
  const [expanded, setExpanded] = useState(true)
  const [committeeAddress, setCommitteeAddress] = useState<CommitteeAddress[]>(originalAddress)
  const [loadingClick] = useState(false)
  const { showErrorMessage } = useGlobalMessageHandler()

  const resetAddresses = (index: number): void => {
    setCommitteeAddress(prevState => {
      return prevState.map((address, i) =>
        i === index ? { ...originalAddress[index] } : address
      )
    })
  }

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const hasMinorityAddress = (): boolean => {
    return committeeAddress.some(address => address.displayOrder === 2)
  }

  const handleAddMinorityAddress = (): void => {
    if (committeeAddress.length === 0 || committeeAddress[0]?.committeeId == null) {
      showErrorMessage('Cannot create minority address: committee ID not found')
      return
    }

    const committeeId = committeeAddress[0].committeeId
    const newAddress = getEmptyCommitteeAddress(committeeId)
    setCommitteeAddress(prevAddresses => [...prevAddresses, newAddress])
  }

  const handleRemoveMinorityAddress = (): void => {
    setCommitteeAddress(prevAddresses => prevAddresses.filter(address => address.id !== 0))
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header"
                          sx={accordionSummaryStyles}>
          <div>
            <Typography variant="h6">Addresses</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent>
            <Stack spacing={2}>
            {committeeAddress?.map((add, index) => (<CommitteeAddressBlock key={add.id} originalBasicInfo={originalBasicInfo} comAddress={add} resetAddress={() => { add.id === 0 ? handleRemoveMinorityAddress() : resetAddresses(index) }} updateAddress={(address: CommitteeAddress) => {
              setCommitteeAddress(prevState => {
                const newState = [...prevState]
                newState[index] = address
                return newState
              })
            }} startDisabled={add.id !== 0} startExpanded={true}/>))}

            {!hasMinorityAddress() && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <ButtonWithIcon
                  nameButton="Add Minority Address"
                  icon={<AddIcon />}
                  onClick={handleAddMinorityAddress}
                  disabled={loadingClick || loading}
                />
              </Box>
            )}
            </Stack>
            </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
